import { useState, useRef, useEffect, type CSSProperties } from 'react'
import { motion } from 'framer-motion'
import { Bot, Mail } from 'lucide-react'

interface HeroSectionProps {
  onCTAClick: () => void
}

function RollingText({ first, second, active, className, style }: { first: string; second: string; active: boolean; className?: string; style?: CSSProperties }) {
  return (
    <div className={`relative overflow-hidden h-6 leading-6 ${className ?? ''}`} style={style}>
      <motion.span
        initial={{ y: '0%', opacity: 1 }}
        animate={{ y: active ? '-100%' : '0%', opacity: active ? 0 : 1 }}
        transition={{ duration: 0.45, ease: 'easeInOut' }}
        className="absolute inset-0 flex items-center justify-center whitespace-nowrap"
        style={{ willChange: 'transform, opacity' }}
      >
        {first}
      </motion.span>
      <motion.span
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: active ? '0%' : '100%', opacity: active ? 1 : 0 }}
        transition={{ duration: 0.45, ease: 'easeInOut' }}
        className="absolute inset-0 flex items-center justify-center whitespace-nowrap"
        style={{ willChange: 'transform, opacity' }}
      >
        {second}
      </motion.span>
    </div>
  )
}

export default function HeroSection({ onCTAClick }: HeroSectionProps) {
  const [isPrimaryHover, setIsPrimaryHover] = useState(false)
  const [isSecondaryHover, setIsSecondaryHover] = useState(false)
  const [primaryMinWidth, setPrimaryMinWidth] = useState<number>(0)
  const [secondaryMinWidth, setSecondaryMinWidth] = useState<number>(0)
  const [primaryTextMinWidth, setPrimaryTextMinWidth] = useState<number>(0)
  const [secondaryTextMinWidth, setSecondaryTextMinWidth] = useState<number>(0)

  const primaryLabel1Ref = useRef<HTMLSpanElement>(null) // Заказать
  const primaryLabel2Ref = useRef<HTMLSpanElement>(null) // Чат-бот
  const secondaryLabel1Ref = useRef<HTMLSpanElement>(null) // Смотреть
  const secondaryLabel2Ref = useRef<HTMLSpanElement>(null) // Демо

  useEffect(() => {
    const getW = (el: HTMLElement | null) => (el ? el.getBoundingClientRect().width : 0)
    const pad = 64 // px-8 left + right
    const border = 4 // account for border-2 on hover
    const iconSpace = 28 // Bot icon (20px) + gap-2 (~8px) for secondary

    const primaryTextW = Math.max(getW(primaryLabel1Ref.current), getW(primaryLabel2Ref.current))
    const secondaryTextW = Math.max(getW(secondaryLabel1Ref.current), getW(secondaryLabel2Ref.current))

    setPrimaryTextMinWidth(Math.ceil(primaryTextW))
    setSecondaryTextMinWidth(Math.ceil(secondaryTextW))

    const primaryW = primaryTextW + pad + border
    const secondaryW = secondaryTextW + pad + border + iconSpace
    setPrimaryMinWidth(Math.ceil(primaryW))
    setSecondaryMinWidth(Math.ceil(secondaryW))
  }, [])

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-blue-50 rounded-full blur-3xl opacity-30" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-indigo-50 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-6"
            >
              <Bot className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Профессиональная разработка</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            >
              Разработка<br />
              Телеграм и Макс ботов<br />
              <span className="text-blue-600">«под ключ»</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-xl text-gray-600 mb-8 max-w-xl"
            >
              для автоматизации бизнес процессов
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-lg text-gray-500 mb-8 max-w-xl leading-relaxed"
            >
              Запускаем MVP за недели, а не месяцы: интеграции с CRM и эквайрингом, 
              автоматизация заявок и платежей прямо в чате
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mb-8"
            >
              {/* Hidden measuring labels to lock widths */}
              <div className="fixed -left-[9999px] -top-[9999px] font-semibold text-base">
                <span ref={primaryLabel1Ref} className="px-0">Заказать</span>
                <span ref={primaryLabel2Ref} className="px-0">Чат-бот</span>
                <span ref={secondaryLabel1Ref} className="px-0">Смотреть</span>
                <span ref={secondaryLabel2Ref} className="px-0">Демо</span>
              </div>

              <button
                onClick={onCTAClick}
                onMouseEnter={() => setIsPrimaryHover(true)}
                onMouseLeave={() => setIsPrimaryHover(false)}
                style={{ width: primaryMinWidth ? `${primaryMinWidth}px` : undefined }}
                className={`open-modal__btn px-8 py-4 rounded-xl font-semibold box-border transition-colors duration-500 ease-in-out hover:scale-105 shadow-lg hover:shadow-xl ${
                  isPrimaryHover
                    ? 'bg-white text-gray-900 border-2 border-gray-200 hover:border-blue-600'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                }`}
              >
                <RollingText
                  first="Заказать"
                  second="Чат-бот"
                  active={isPrimaryHover}
                  className="shrink-0"
                  style={{ width: primaryTextMinWidth || 96 }}
                />
              </button>

              <a
                href="https://t.me/softrest_bot"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setIsSecondaryHover(true)}
                onMouseLeave={() => setIsSecondaryHover(false)}
                style={{ width: secondaryMinWidth ? `${secondaryMinWidth}px` : undefined }}
                className={`px-8 py-4 rounded-xl font-semibold box-border transition-colors duration-500 ease-in-out flex items-center justify-center gap-2 hover:scale-105 shadow-lg hover:shadow-xl ${
                  isSecondaryHover
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-2 border-transparent'
                    : 'bg-white text-gray-900 border-2 border-gray-200 hover:border-blue-600'
                }`}
              >
                <Bot className="w-5 h-5" />
                <RollingText
                  first="Смотреть"
                  second="Демо"
                  active={isSecondaryHover}
                  className="shrink-0"
                  style={{ width: secondaryTextMinWidth ? secondaryTextMinWidth : undefined }}
                />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="flex items-center gap-2 text-gray-500"
            >
              <Mail className="w-5 h-5" />
              <a href="mailto:info@softrest.ru" className="hover:text-blue-600 transition-colors">
                info@softrest.ru
              </a>
            </motion.div>
          </motion.div>

          {/* Right illustration */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative w-full h-[600px] flex items-center justify-center">
              {/* Decorative elements */}
              <motion.div
                animate={{
                  y: [0, -20, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute top-10 right-10 w-20 h-20 bg-blue-500 rounded-2xl opacity-20"
              />
              <motion.div
                animate={{
                  y: [0, 20, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
                className="absolute bottom-20 left-10 w-16 h-16 bg-indigo-500 rounded-full opacity-20"
              />
              
              {/* Main illustration */}
              <div className="relative bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl p-12 shadow-2xl">
                <Bot className="w-64 h-64 text-white" strokeWidth={1} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-2 bg-gray-400 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  )
}
